import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import StatsCard from './components/StatsCard';
import ApplicationCard from './components/ApplicationCard';
import JobCard from './components/JobCard';
import ActivityItem from './components/ActivityItem';
import EarningsChart from './components/EarningsChart';
import ProfileCompletionCard from './components/ProfileCompletionCard';
import QuickActionButton from './components/QuickActionButton';
import MessagePreview from './components/MessagePreview';
import { applicationsService, jobsService, earningsService, workerProfilesService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { notificationsService } from '../../services/notificationsService';
import * as messagingService from '../../services/messagingService';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState('provider');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [workerProfile, setWorkerProfile] = useState(null);
  const [activities, setActivities] = useState([]);
  const [messages, setMessages] = useState([]);

  // Fetch current user and data
  useEffect(() => {
    fetchUserData();
  }, [userType]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Dev mode bypass - create mock user for development
      const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
      let user;

      if (isDevMode) {
        // Mock user for dev mode - using valid UUID format
        user = {
          id: '00000000-0000-0000-0000-000000000001',
          email: 'dev@example.com',
          user_metadata: { full_name: 'Dev User' }
        };
      } else {
        const { data: { user: authUser } } = await supabase?.auth?.getUser();
        if (!authUser) {
          setError('Please log in to view your dashboard');
          return;
        }
        user = authUser;
      }

      setCurrentUser(user);

      if (userType === 'provider' || userType === 'both') {
        const [appsData, earningsData, profileData] = await Promise.all([
        applicationsService?.getUserApplications(user?.id),
        earningsService?.getUserEarnings(user?.id),
        workerProfilesService?.getWorkerProfile(user?.id)]);
        setApplications(appsData || []);
        setEarnings(earningsData || []);
        setWorkerProfile(profileData);
      }

      if (userType === 'seeker' || userType === 'both') {
        const jobsData = await jobsService?.getUserJobs(user?.id);
        setJobs(jobsData || []);
      }

      // Fetch activities (notifications)
      const notificationsData = await notificationsService?.getUserNotifications(user?.id);
      const activitiesData = (notificationsData || [])?.slice(0, 5)?.map((notif) => ({
        type: notif?.type === 'job_application' ? 'application' : notif?.type === 'message' ? 'message' : notif?.type === 'payment' ? 'payment' : 'job',
        title: notif?.title,
        description: notif?.message,
        timestamp: notif?.createdAt,
        priority: notif?.priority,
        avatar: null,
        avatarAlt: 'Activity avatar'
      }));
      setActivities(activitiesData);

      // Fetch recent messages
      const conversationsData = await messagingService?.getUserConversations(user?.id);
      const messagesData = (conversationsData || [])?.slice(0, 3)?.map((conv) => ({
        name: conv?.otherUser?.fullName || 'User',
        preview: conv?.lastMessage?.content || 'No messages yet',
        timestamp: conv?.lastMessageAt,
        avatar: conv?.otherUser?.avatarUrl || "https://img.rocket.new/generatedImages/rocket_gen_img_1e6008c7e-1768740999976.png",
        avatarAlt: `${conv?.otherUser?.fullName || 'User'} avatar`,
        unread: conv?.unreadCount > 0,
        online: false
      }));
      setMessages(messagesData);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats from real data
  const providerStats = [
  {
    icon: 'FileText',
    label: 'Active Applications',
    value: applications?.filter((app) => app?.status === 'pending')?.length?.toString(),
    trend: 'up',
    trendValue: '+' + applications?.filter((app) => {
      const date = new Date(app?.createdAt);
      const weekAgo = new Date();
      weekAgo?.setDate(weekAgo?.getDate() - 7);
      return date > weekAgo;
    })?.length,
    color: 'primary'
  },
  {
    icon: 'Star',
    label: 'Average Rating',
    value: workerProfile?.rating?.toFixed(1) || '0.0',
    trend: 'up',
    trendValue: '+0.2',
    color: 'warning'
  },
  {
    icon: 'CheckCircle',
    label: 'Completed Jobs',
    value: workerProfile?.totalJobsCompleted?.toString() || '0',
    trend: 'up',
    trendValue: '+' + applications?.filter((app) => app?.status === 'completed')?.length,
    color: 'success'
  },
  {
    icon: 'DollarSign',
    label: 'Total Earnings',
    value: '₱' + earnings?.reduce((sum, e) => sum + parseFloat(e?.amount || 0), 0)?.toLocaleString(),
    trend: 'up',
    trendValue: '+12%',
    color: 'accent'
  }];


  const seekerStats = [
  {
    icon: 'Briefcase',
    label: 'Active Jobs',
    value: jobs?.filter((job) => job?.status === 'active')?.length?.toString(),
    trend: 'up',
    trendValue: '+1',
    color: 'primary'
  },
  {
    icon: 'Users',
    label: 'Total Applicants',
    value: jobs?.reduce((sum, job) => sum + (job?.applicantCount || 0), 0)?.toString(),
    trend: 'up',
    trendValue: '+8',
    color: 'accent'
  },
  {
    icon: 'CheckCircle',
    label: 'Completed Projects',
    value: jobs?.filter((job) => job?.status === 'completed')?.length?.toString(),
    trend: 'up',
    trendValue: '+2',
    color: 'success'
  },
  {
    icon: 'DollarSign',
    label: 'Total Spent',
    value: '₱' + jobs?.reduce((sum, job) => sum + (job?.budgetMax || 0), 0)?.toLocaleString(),
    trend: 'up',
    trendValue: '+18%',
    color: 'warning'
  }];


  const stats = userType === 'provider' ? providerStats : seekerStats;

  // Transform applications for display
  const providerApplications = applications?.map((app) => ({
    id: app?.id,
    title: app?.jobs?.title || 'Job Title',
    clientName: app?.jobs?.userProfiles?.fullName || 'Client',
    location: app?.jobs?.city || 'Location',
    rate: app?.proposedRate,
    status: app?.status,
    appliedDate: app?.createdAt,
    availability: app?.availability,
    coverLetter: app?.coverLetter,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b813928-1770140448408.png",
    imageAlt: 'Job application image'
  }));

  // Transform jobs for display
  const seekerJobs = jobs?.map((job) => ({
    id: job?.id,
    title: job?.title,
    description: job?.description,
    location: job?.city,
    budget: job?.budgetMax,
    status: job?.status,
    applicants: job?.applicantCount,
    views: job?.views || 0,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b856f364-1768842096495.png",
    imageAlt: 'Job posting image'
  }));

  // Calculate profile completion
  const calculateProfileCompletion = () => {
    if (userType === 'provider') {
      let completion = 40; // Base completion
      if (workerProfile?.bio) completion += 15;
      if (workerProfile?.skills?.length > 0) completion += 15;
      if (workerProfile?.experienceYears > 0) completion += 15;
      if (workerProfile?.isVerified) completion += 15;
      return completion;
    } else {
      let completion = 60; // Base completion for seekers
      if (jobs?.length > 0) completion += 20;
      if (currentUser?.email) completion += 20;
      return completion;
    }
  };

  const profileCompletion = calculateProfileCompletion();

  const handleViewProfile = () => {
    if (currentUser?.id) {
      navigate(`/user-profile?userId=${currentUser?.id}`);
    }
  };

  const tabs = [
  { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
  { id: userType === 'provider' ? 'applications' : 'jobs', label: userType === 'provider' ? 'My Applications' : 'My Jobs', icon: userType === 'provider' ? 'FileText' : 'Briefcase' },
  { id: 'activity', label: 'Activity', icon: 'Activity' },
  { id: 'earnings', label: userType === 'provider' ? 'Earnings' : 'Spending', icon: 'DollarSign' }];


  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Dashboard - Jobs Platform</title>
      </Helmet>
      <Header />
      {loading ?
      <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div> :
      error ?
      <div className="container mx-auto px-4 py-8">
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-6 text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <p className="text-destructive font-medium mb-2">Error loading dashboard</p>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div> :

      <div className="container mx-auto px-3 md:px-4 py-3 md:py-8">
          {/* User Type Toggle */}
          <div className="flex items-center justify-between mb-3 md:mb-6">
            <div>
              <h1 className="text-lg md:text-3xl font-heading font-bold text-foreground mb-1 md:mb-2">
                Welcome back, {currentUser?.email?.split('@')?.[0] || 'User'}!
              </h1>
              <p className="text-xs md:text-base text-muted-foreground">
                Here's what's happening with your account today.
              </p>
            </div>
            <div className="flex gap-1.5 md:gap-2">
              <button
              onClick={() => setUserType('provider')}
              className={`px-2.5 py-1.5 md:px-4 md:py-2 rounded-md text-xs md:text-sm font-medium transition-smooth ${
              userType === 'provider' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
              }>
              
                Provider
              </button>
              <button
              onClick={() => setUserType('seeker')}
              className={`px-2.5 py-1.5 md:px-4 md:py-2 rounded-md text-xs md:text-sm font-medium transition-smooth ${
              userType === 'seeker' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
              }>
              
                Seeker
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-3 md:mb-8">
            {stats?.map((stat, index) =>
          <StatsCard key={index} {...stat} />
          )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-4 gap-2 md:gap-4 mb-3 md:mb-8">
            <QuickActionButton
            icon="Search"
            label="Find Jobs"
            onClick={() => navigate('/job-board')}
            variant="default" />
          
            <QuickActionButton
            icon="PlusCircle"
            label="Post Job"
            onClick={() => navigate('/post-job')}
            variant="outline" />
          
            <QuickActionButton
            icon="MessageSquare"
            label="Messages"
            onClick={() => navigate('/messages')}
            variant="outline" />
          
            <QuickActionButton
            icon="User"
            label="Profile"
            onClick={handleViewProfile}
            variant="outline" />
          
          </div>

          {/* Tabs */}
          <div className="flex gap-1.5 md:gap-2 mb-3 md:mb-6 overflow-x-auto">
            {tabs?.map((tab) =>
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-1.5 md:gap-2 px-2.5 py-1.5 md:px-4 md:py-2 rounded-md text-xs md:text-sm font-medium transition-smooth whitespace-nowrap ${
            activeTab === tab?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
            }>
            
                <Icon name={tab?.icon} size={14} className="md:w-4 md:h-4" />
                <span className="hidden sm:inline">{tab?.label}</span>
              </button>
          )}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' &&
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-3 md:space-y-6">
                {/* Recent Applications/Jobs */}
                <div className="bg-card rounded-md p-3 md:p-6 shadow-warm">
                  <div className="flex items-center justify-between mb-2 md:mb-4">
                    <h2 className="text-base md:text-xl font-heading font-semibold text-foreground">
                      {userType === 'provider' ? 'Recent Applications' : 'Recent Jobs'}
                    </h2>
                    <button
                  onClick={() => setActiveTab(userType === 'provider' ? 'applications' : 'jobs')}
                  className="text-xs md:text-sm text-primary hover:underline">
                  
                      View All
                    </button>
                  </div>
                  <div className="space-y-2 md:space-y-4">
                    {userType === 'provider' ?
                providerApplications?.length > 0 ?
                providerApplications?.slice(0, 3)?.map((app) =>
                <ApplicationCard key={app?.id} application={app} userType={userType} />
                ) :

                <p className="text-center text-muted-foreground py-4 md:py-8 text-xs md:text-base">No applications yet</p> :


                seekerJobs?.length > 0 ?
                seekerJobs?.slice(0, 3)?.map((job) =>
                <JobCard key={job?.id} job={job} />
                ) :

                <p className="text-center text-muted-foreground py-4 md:py-8 text-xs md:text-base">No jobs posted yet</p>

                }
                  </div>
                </div>

                {/* Earnings Chart */}
                <EarningsChart userType={userType} earnings={earnings} />
              </div>

              {/* Sidebar */}
              <div className="space-y-3 md:space-y-6">
                {/* Profile Completion */}
                <ProfileCompletionCard completion={profileCompletion} userType={userType} />

                {/* Recent Messages */}
                <div className="bg-card rounded-md p-3 md:p-6 shadow-warm">
                  <div className="flex items-center justify-between mb-2 md:mb-4">
                    <h2 className="text-base md:text-xl font-heading font-semibold text-foreground">
                      Recent Messages
                    </h2>
                    <button
                  onClick={() => navigate('/messages')}
                  className="text-xs md:text-sm text-primary hover:underline">
                  
                      View All
                    </button>
                  </div>
                  <div className="space-y-2 md:space-y-3">
                    {messages?.length > 0 ?
                messages?.map((msg, index) =>
                <MessagePreview key={index} message={msg} />
                ) :

                <p className="text-center text-muted-foreground py-2 md:py-4 text-xs md:text-base">No messages yet</p>
                }
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-card rounded-md p-3 md:p-6 shadow-warm">
                  <div className="flex items-center justify-between mb-2 md:mb-4">
                    <h2 className="text-base md:text-xl font-heading font-semibold text-foreground">
                      Recent Activity
                    </h2>
                    <button
                  onClick={() => setActiveTab('activity')}
                  className="text-xs md:text-sm text-primary hover:underline">
                  
                      View All
                    </button>
                  </div>
                  <div className="space-y-2 md:space-y-3">
                    {activities?.length > 0 ?
                activities?.map((activity, index) =>
                <ActivityItem key={index} {...activity} />
                ) :

                <p className="text-center text-muted-foreground py-2 md:py-4 text-xs md:text-base">No recent activity</p>
                }
                  </div>
                </div>
              </div>
            </div>
        }

          {activeTab === 'applications' && userType === 'provider' &&
        <div className="space-y-2 md:space-y-4">
              <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground mb-2 md:mb-4">My Applications</h2>
              {providerApplications?.length > 0 ?
          providerApplications?.map((app) =>
          <ApplicationCard key={app?.id} application={app} userType={userType} />
          ) :

          <div className="bg-card rounded-md p-6 md:p-12 text-center">
                  <Icon name="FileText" size={36} className="md:w-12 md:h-12 text-muted-foreground mx-auto mb-2 md:mb-4" />
                  <p className="text-muted-foreground mb-2 md:mb-4 text-xs md:text-base">You haven't applied to any jobs yet</p>
                  <button
              onClick={() => navigate('/job-board')}
              className="px-4 py-2 md:px-6 md:py-3 rounded-md bg-primary text-primary-foreground text-xs md:text-base font-medium hover:opacity-90 transition-smooth">
              
                    Browse Jobs
                  </button>
                </div>
          }
            </div>
        }

          {activeTab === 'jobs' && userType === 'seeker' &&
        <div className="space-y-2 md:space-y-4">
              <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground mb-2 md:mb-4">My Jobs</h2>
              {seekerJobs?.length > 0 ?
          seekerJobs?.map((job) =>
          <JobCard key={job?.id} job={job} />
          ) :

          <div className="bg-card rounded-md p-6 md:p-12 text-center">
                  <Icon name="Briefcase" size={36} className="md:w-12 md:h-12 text-muted-foreground mx-auto mb-2 md:mb-4" />
                  <p className="text-muted-foreground mb-2 md:mb-4 text-xs md:text-base">You haven't posted any jobs yet</p>
                  <button
              onClick={() => navigate('/post-job')}
              className="px-4 py-2 md:px-6 md:py-3 rounded-md bg-primary text-primary-foreground text-xs md:text-base font-medium hover:opacity-90 transition-smooth">
              
                    Post a Job
                  </button>
                </div>
          }
            </div>
        }

          {activeTab === 'activity' &&
        <div className="space-y-2 md:space-y-4">
              <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground mb-2 md:mb-4">Activity Feed</h2>
              {activities?.length > 0 ?
          activities?.map((activity, index) =>
          <ActivityItem key={index} {...activity} />
          ) :

          <div className="bg-card rounded-md p-6 md:p-12 text-center">
                  <Icon name="Activity" size={36} className="md:w-12 md:h-12 text-muted-foreground mx-auto mb-2 md:mb-4" />
                  <p className="text-muted-foreground text-xs md:text-base">No recent activity</p>
                </div>
          }
            </div>
        }

          {activeTab === 'earnings' &&
        <div className="space-y-3 md:space-y-6">
              <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground mb-2 md:mb-4">
                {userType === 'provider' ? 'Earnings' : 'Spending'} Overview
              </h2>
              <EarningsChart userType={userType} earnings={earnings} />
              
              {/* Earnings/Spending Details */}
              <div className="bg-card rounded-md p-3 md:p-6 shadow-warm">
                <h3 className="text-base md:text-xl font-heading font-semibold text-foreground mb-2 md:mb-4">
                  Transaction History
                </h3>
                <div className="space-y-2 md:space-y-3">
                  {earnings?.length > 0 ?
              earnings?.map((earning) =>
              <div key={earning?.id} className="flex items-center justify-between p-2 md:p-4 bg-muted rounded-md">
                        <div>
                          <p className="font-medium text-foreground text-xs md:text-base">{earning?.description || 'Transaction'}</p>
                          <p className="text-xs md:text-sm text-muted-foreground">
                            {new Date(earning?.createdAt)?.toLocaleDateString('en-PH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm md:text-lg font-bold text-foreground">₱{parseFloat(earning?.amount)?.toLocaleString()}</p>
                          <p className="text-xs md:text-sm text-muted-foreground">{earning?.type}</p>
                        </div>
                      </div>
              ) :

              <p className="text-center text-muted-foreground py-4 md:py-8 text-xs md:text-base">No transactions yet</p>
              }
                </div>
              </div>
            </div>
        }
        </div>
      }
    </div>);


};

export default UserDashboard;